import { useEffect, useRef, useCallback, useState } from "react";
import { Row, Typography } from "antd";
import i18n from "i18n";
import _invoke from "lodash/invoke";
import _isEmpty from "lodash/isEmpty";
import _get from "lodash/get";
import { v4 as uuidv4 } from "uuid";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { usePrevious, useSocket } from "hooks";
import {
  getConversationListRequest,
  conversationSelectors,
  messageSelectors,
  getMessageListRequest,
  sendMessageRequest,
  updateMessage,
  getConversationDetailRequest,
  seenConversationRequest,
} from "providers/MessengerProvider/slice";
import { getBillingListRequest } from "providers/BillingProvider/slice";
import "./styles.less";
import Helper from "utils/helpers";
import MessageInput from "./MessageList/MessageInput";
import MessageList from "./MessageList";
import ConversationList from "./ConversationList";

const Messenger = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isSending, setIsSending] = useState(false);
  const [optimisticMessages, setOptimisticMessages] = useState([]);
  const [isGettingList, setIsGettingList] = useState(false);
  const formRef = useRef();
  const currentUser = useSelector((state) => state.auth.currentUser);
  const currentUserId = _get(currentUser, "_id");
  const currentUserRole = _get(currentUser, "role");
  const { socket, isConnected } = useSocket();

  const { conversationId } = useParams();
  const conversationIds = useSelector(conversationSelectors.selectIds);
  const conversationEntities = useSelector(
    conversationSelectors.selectEntities
  );
  const conversationHasMore = useSelector(
    (state) => state.messenger.conversations.hasMore
  );
  const conversationPage = useSelector(
    (state) => state.messenger.conversations.page
  );
  const opponentUserId = _get(
    conversationEntities[conversationId],
    "member[0]._id"
  );

  const messageIds = useSelector(messageSelectors.selectIds);
  const messageEntities = useSelector(messageSelectors.selectEntities);
  const messageHasMore = useSelector(
    (state) => state.messenger.messages.hasMore
  );
  const lastMessageTime = useSelector(
    (state) => state.messenger.messages.lastMessageTime
  );
  const firstGetList = useSelector(
    (state) => state.messenger.firstGetList
  );

  const firstConversationId = conversationIds[0];
  const prevFirstConversationId = usePrevious(firstConversationId);

  // Socket: new_message handler
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      socket.emit("identity");
      socket.emit("subscribe", { room: conversationId });

      const handleNewMessage = (data) => {
        const dataConversationId = _get(data, "message.conversationId");
        if (dataConversationId === conversationId) {
          // Remove optimistic message when real message arrives
          const tempId = _get(data, "message.tempId");
          if (tempId) {
            setOptimisticMessages((prev) =>
              prev.filter((msg) => msg.tempId !== tempId)
            );
          }
          dispatch(updateMessage({ ...data }));
        }
      };

      socket.on("new_message", handleNewMessage);
      return () => {
        socket.off("new_message", handleNewMessage);
      };
    }
  }, [conversationId, currentUserId, dispatch, socket, isConnected]);

  // Socket: conversation-change handler
  useEffect(() => {
    if (currentUserId && socket && isConnected) {
      const handleConversationChange = (data) => {
        dispatch(getConversationDetailRequest(data));
      };
      socket.on("conversation-change", handleConversationChange);
      return () => {
        socket.off("conversation-change", handleConversationChange);
      };
    }
  }, [currentUserId, dispatch, socket, isConnected]);

  useEffect(() => {
    if (!prevFirstConversationId && firstConversationId && !conversationId) {
      navigate(`/cms_viego_support/viego_messengers/${firstConversationId}`, {
        replace: true,
      });
    }
  }, [conversationId, firstConversationId, navigate, prevFirstConversationId]);

  useEffect(() => {
    dispatch(
      getConversationListRequest({
        limit: 20,
        page: 1,
        type: "VIETGO_TO_USER",
        role: "fonehouse",
      })
    );
  }, [dispatch]);

  useEffect(() => {
    if (conversationId) {
      dispatch(seenConversationRequest({ conversationId }));
      dispatch(getMessageListRequest({ limit: 20, conversationId }));
    }
  }, [conversationId, dispatch]);

  useEffect(() => {
    if (opponentUserId) {
      dispatch(
        getBillingListRequest({ userId: opponentUserId, limit: 1, page: 1 })
      );
    }
  }, [dispatch, opponentUserId]);

  const handleGetMessageList = useCallback(() => {
    dispatch(
      getMessageListRequest({
        limit: 20,
        time: lastMessageTime,
        conversationId,
      })
    );
  }, [conversationId, dispatch, lastMessageTime]);

  const handleGetConversationList = useCallback(() => {
    if (isGettingList) return;
    setIsGettingList(true);
    dispatch(
      getConversationListRequest({
        limit: 20,
        page: conversationPage + 1,
        type: "VIETGO_TO_USER",
        role: "fonehouse",
      })
    );
    // Reset after a short delay
    setTimeout(() => setIsGettingList(false), 1000);
  }, [conversationPage, dispatch, isGettingList]);

  const handleConversationCellClick = useCallback(
    (_conversationId) => {
      navigate(`/cms_viego_support/viego_messengers/${_conversationId}`, {
        replace: true,
      });
    },
    [navigate]
  );

  const handleSendMessage = useCallback(
    async (values) => {
      // Double-submit guard
      if (isSending) return;

      try {
        formRef.current.resetForm();
        const tempId = uuidv4();
        const trimmedMessage = _invoke(values.message, "trim");

        // Create optimistic message immediately
        if (trimmedMessage || !_isEmpty(values.attachments)) {
          const optimisticMsg = {
            _id: tempId,
            tempId,
            text: trimmedMessage,
            type: "text",
            conversationId,
            createdAt: new Date().toISOString(),
            sender: { _id: currentUserId, name: currentUser?.name },
            isOptimistic: true,
            isAdmin: true,
          };
          setOptimisticMessages((prev) => [optimisticMsg, ...prev]);
        }

        let attachments;
        if (!_isEmpty(values.attachments)) {
          let promises = [];
          const imageFiles = values.attachments.filter(
            (file) => file.type === "image"
          );
          const videoFiles = values.attachments.filter(
            (file) => file.type === "video"
          );
          if (!_isEmpty(videoFiles)) {
            promises = promises.concat(
              videoFiles.map((file) => Helper.uploadVideo(file))
            );
          }
          if (!_isEmpty(imageFiles)) {
            promises = promises.concat(
              imageFiles.map((file) => Helper.uploadFile(file))
            );
          }
          setUploading(true);
          setUploadProgress(0);
          const result = await Promise.all(promises);
          attachments = result.map((res) => ({
            type: res.attachmentType,
            data:
              res.attachmentType === "image"
                ? { imageUrl: res.url }
                : { videoUrl: res.url },
          }));
          setUploading(false);
          setUploadProgress(100);
        }

        if (trimmedMessage || !_isEmpty(attachments)) {
          setIsSending(true);
          dispatch(
            sendMessageRequest({
              type: "text",
              text: trimmedMessage || null,
              conversationId,
              attachments,
              tempId,
            })
          )
            .then((result) => {
              // Remove optimistic message on success
              setOptimisticMessages((prev) =>
                prev.filter((msg) => msg.tempId !== tempId)
              );
              socket?.emit("new_message_admin", {
                room: conversationId,
                messageId: result._id,
              });
            })
            .catch(() => {
              // Mark optimistic message as failed
              setOptimisticMessages((prev) =>
                prev.map((msg) =>
                  msg.tempId === tempId ? { ...msg, isFailed: true } : msg
                )
              );
            })
            .finally(() => {
              setIsSending(false);
            });
        }
      } catch (error) {
        setUploading(false);
        setIsSending(false);
        console.error(error);
      }
    },
    [conversationId, currentUser?.name, currentUserId, dispatch, isSending, socket]
  );

  // Retry failed message
  const handleRetryMessage = useCallback(
    (failedMsg) => {
      // Remove failed message from optimistic list
      setOptimisticMessages((prev) =>
        prev.filter((msg) => msg.tempId !== failedMsg.tempId)
      );
      // Resend
      handleSendMessage({ message: failedMsg.text, attachments: [] });
    },
    [handleSendMessage]
  );

  return (
    <div className="messenger-page">
      <Row className="news-fonehouse-list-header" justify="space-between">
        <Typography.Title level={4}>
          {i18n.t("messenger.pageTitle")}
        </Typography.Title>
      </Row>
      <Row className="color-legend" style={{ marginBottom: 16, padding: "0 16px" }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: 16, 
          fontSize: 12,
          color: "#666"
        }}>
          <span style={{ fontWeight: 500 }}>Chú thích màu sắc:</span>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              backgroundColor: "#bfffe2", 
              borderRadius: 4,
              border: "1px solid #ddd"
            }}></div>
            <span>Tin nhắn chưa đọc</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              backgroundColor: "#ffebee", 
              borderRadius: 4,
              border: "1px solid #ddd"
            }}></div>
            <span>Đã đọc nhưng chưa trả lời</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ 
              width: 16, 
              height: 16, 
              backgroundColor: "transparent", 
              borderRadius: 4,
              border: "1px solid #ddd"
            }}></div>
            <span>Đã đọc và đã trả lời</span>
          </div>
        </div>
      </Row>
      <div className="message-page-content">
        <ConversationList
          activeConversationId={conversationId}
          onConversationCellClick={handleConversationCellClick}
          conversationIds={conversationIds}
          hasMore={conversationHasMore}
          getConversationList={handleGetConversationList}
          conversationEntities={conversationEntities}
          currentUserId={currentUserId}
          isGettingList={isGettingList}
        />
        <div className="conversation-container">
          <MessageList
            hasMore={messageHasMore}
            getMessageList={handleGetMessageList}
            messageIds={messageIds}
            currentUserId={currentUserId}
            currentUserRole={currentUserRole}
            messageEntities={messageEntities}
            optimisticMessages={optimisticMessages}
            onRetry={handleRetryMessage}
            firstGetList={firstGetList}
          />
          <MessageInput
            conversationId={conversationId}
            isSending={isSending}
            uploadProgress={uploadProgress}
            ref={formRef}
            uploading={uploading}
            onClick={handleSendMessage}
          />
        </div>
      </div>
    </div>
  );
};

export default Messenger;
